import { AlertTriangle, Check, Loader2, Send, Sparkles, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useHeatherPanel } from '../../context/HeatherPanelContext';
import {
  confirmToolCall,
  createRun,
  streamMessage,
  type ConfirmCard,
} from '../../lib/heatherClient';

type Turn =
  | { kind: 'user'; text: string }
  | { kind: 'assistant'; text: string; streaming: boolean }
  | { kind: 'confirm'; card: ConfirmCard; resolved?: 'approved' | 'rejected' | 'failed' }
  | { kind: 'error'; text: string }
  | { kind: 'done'; reason: string; costCents?: number };

export function HeatherPanel() {
  const { open, setOpen } = useHeatherPanel();
  const [runId, setRunId] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [starting, setStarting] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const ensureRun = useCallback(async (): Promise<string | null> => {
    if (runId) return runId;
    setStarting(true);
    try {
      const run = await createRun();
      setRunId(run.id);
      return run.id;
    } catch (err) {
      setTurns((t) => [...t, { kind: 'error', text: toErrorText(err) }]);
      return null;
    } finally {
      setStarting(false);
    }
  }, [runId]);

  const consumeStream = useCallback(
    async (rid: string, body: { message?: string; resumeToolCallId?: string }) => {
      setStreaming(true);
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Push a streaming assistant turn that subsequent text deltas append to.
      setTurns((t) => [...t, { kind: 'assistant', text: '', streaming: true }]);

      try {
        for await (const ev of streamMessage(rid, body, controller.signal)) {
          if (ev.event === 'text') {
            const delta = (ev.data as { delta?: string })?.delta ?? '';
            setTurns((t) => patchLastAssistant(t, (prev) => prev + delta));
          } else if (ev.event === 'confirm') {
            setTurns((t) => [
              ...finalizeLastAssistant(t),
              { kind: 'confirm', card: ev.data as ConfirmCard },
            ]);
          } else if (ev.event === 'done') {
            const d = ev.data as { reason?: string; costCents?: number };
            setTurns((t) => [
              ...finalizeLastAssistant(t),
              { kind: 'done', reason: d.reason ?? 'complete', costCents: d.costCents },
            ]);
          } else if (ev.event === 'error') {
            const d = ev.data as { message?: string; code?: string };
            setTurns((t) => [
              ...finalizeLastAssistant(t),
              { kind: 'error', text: d.message ?? d.code ?? 'Unknown error' },
            ]);
          }
        }
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return;
        setTurns((t) => [
          ...finalizeLastAssistant(t),
          { kind: 'error', text: toErrorText(err) },
        ]);
      } finally {
        setStreaming(false);
        setTurns((t) => finalizeLastAssistant(t));
      }
    },
    [],
  );

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    const rid = await ensureRun();
    if (!rid) return;
    setTurns((t) => [...t, { kind: 'user', text }]);
    await consumeStream(rid, { message: text });
  }, [input, streaming, ensureRun, consumeStream]);

  const handleConfirm = useCallback(
    async (toolCallId: string, action: 'approve' | 'reject') => {
      if (!runId) return;
      try {
        await confirmToolCall(runId, toolCallId, action);
      } catch (err) {
        setTurns((t) => {
          const mapped: Turn[] = t.map((turn) =>
            turn.kind === 'confirm' && turn.card.toolCallId === toolCallId
              ? { ...turn, resolved: 'failed' as const }
              : turn,
          );
          return [...mapped, { kind: 'error', text: toErrorText(err) }];
        });
        return;
      }
      setTurns((t): Turn[] =>
        t.map((turn) =>
          turn.kind === 'confirm' && turn.card.toolCallId === toolCallId
            ? { ...turn, resolved: action === 'approve' ? ('approved' as const) : ('rejected' as const) }
            : turn,
        ),
      );
      await consumeStream(runId, { resumeToolCallId: toolCallId });
    },
    [runId, consumeStream],
  );

  const handleNewChat = useCallback(() => {
    abortRef.current?.abort();
    setRunId(null);
    setTurns([]);
    setInput('');
  }, []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 pointer-events-none">
      <div
        className="absolute inset-0 bg-navy-900/30 pointer-events-auto"
        onClick={() => setOpen(false)}
      />
      <aside
        className="absolute right-0 top-0 h-full w-full sm:w-[28rem] bg-cream-100 shadow-cardHover flex flex-col pointer-events-auto"
        aria-label="Heather assistant"
      >
        <header className="flex items-center justify-between px-4 h-14 border-b border-navy-100 bg-navy-900 text-cream-100">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-rose-400" />
            <span className="font-display italic text-lg">Heather</span>
            <span className="text-xs text-cream-300">admin assistant</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleNewChat}
              className="text-xs px-2 py-1 rounded hover:bg-navy-800 text-cream-200"
              disabled={streaming}
            >
              New chat
            </button>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded hover:bg-navy-800"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 text-sm">
          {turns.length === 0 && !starting && (
            <div className="text-navy-600 text-center pt-8 space-y-2">
              <Sparkles size={22} className="mx-auto text-rose-500" />
              <p>Ask Heather to run your club.</p>
              <p className="text-xs text-navy-500">
                Try: &ldquo;who&rsquo;s behind on dues?&rdquo;, &ldquo;schedule 8 Wednesday
                game nights at 7pm&rdquo;, &ldquo;cancel the lesson tomorrow&rdquo;.
              </p>
            </div>
          )}

          {turns.map((t, i) => (
            <TurnView key={i} turn={t} onConfirm={handleConfirm} streaming={streaming} />
          ))}

          {starting && (
            <div className="flex items-center gap-2 text-navy-600 text-xs">
              <Loader2 size={14} className="animate-spin" /> Starting run...
            </div>
          )}
        </div>

        <footer className="border-t border-navy-100 p-3 bg-cream-50">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleSend();
            }}
            className="flex gap-2"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Heather..."
              disabled={streaming}
              className="flex-1 px-3 py-2 rounded-lg border border-navy-200 bg-white focus:outline-none focus:ring-2 focus:ring-rose-500/50 disabled:bg-navy-50"
            />
            <button
              type="submit"
              disabled={!input.trim() || streaming}
              className="px-3 py-2 rounded-lg bg-rose-500 text-cream-100 hover:bg-rose-600 disabled:bg-navy-300 disabled:cursor-not-allowed"
              aria-label="Send"
            >
              {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
          <p className="text-[10px] text-navy-500 mt-2">
            ⌘K to toggle · destructive actions require approval
          </p>
        </footer>
      </aside>
    </div>
  );
}

function TurnView({
  turn,
  onConfirm,
  streaming,
}: {
  turn: Turn;
  onConfirm: (toolCallId: string, action: 'approve' | 'reject') => void;
  streaming: boolean;
}) {
  if (turn.kind === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl bg-rose-500 text-cream-100 px-3 py-2 whitespace-pre-wrap break-words">
          {turn.text}
        </div>
      </div>
    );
  }
  if (turn.kind === 'assistant') {
    return (
      <div className="flex justify-start">
        <div className="max-w-[90%] rounded-2xl bg-white border border-navy-100 px-3 py-2 whitespace-pre-wrap break-words">
          {turn.text || (turn.streaming ? <span className="text-navy-400">…</span> : null)}
        </div>
      </div>
    );
  }
  if (turn.kind === 'confirm') {
    const resolved = turn.resolved;
    return (
      <div className="rounded-xl border-2 border-rose-400 bg-rose-50 px-3 py-3">
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} className="text-rose-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-rose-900 uppercase tracking-wide">
              Confirm: {turn.card.toolName}
            </div>
            <div className="text-sm text-navy-900 mt-1">{turn.card.humanSummary}</div>
            <details className="mt-2">
              <summary className="text-[11px] text-navy-600 cursor-pointer">input</summary>
              <pre className="text-[11px] mt-1 bg-white/60 rounded p-2 overflow-x-auto">
                {JSON.stringify(turn.card.input, null, 2)}
              </pre>
            </details>
            {resolved ? (
              <div className="mt-2 text-xs font-medium">
                {resolved === 'approved' && (
                  <span className="text-green-700 flex items-center gap-1">
                    <Check size={12} /> Approved
                  </span>
                )}
                {resolved === 'rejected' && <span className="text-navy-600">Rejected</span>}
                {resolved === 'failed' && <span className="text-red-600">Failed</span>}
              </div>
            ) : (
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => onConfirm(turn.card.toolCallId, 'approve')}
                  disabled={streaming}
                  className="px-3 py-1.5 rounded bg-rose-500 text-cream-100 text-xs font-medium hover:bg-rose-600 disabled:opacity-50"
                >
                  Approve
                </button>
                <button
                  onClick={() => onConfirm(turn.card.toolCallId, 'reject')}
                  disabled={streaming}
                  className="px-3 py-1.5 rounded bg-white border border-navy-200 text-navy-700 text-xs font-medium hover:bg-navy-50 disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  if (turn.kind === 'error') {
    return (
      <div className="rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
        {turn.text}
      </div>
    );
  }
  if (turn.kind === 'done') {
    if (turn.reason === 'complete') return null;
    return (
      <div className="text-center text-[11px] text-navy-500">
        {turn.reason === 'awaiting_confirm'
          ? 'Awaiting your decision…'
          : `Stopped: ${turn.reason}`}
        {typeof turn.costCents === 'number' ? ` · ${(turn.costCents / 100).toFixed(2)}¢` : ''}
      </div>
    );
  }
  return null;
}

function patchLastAssistant(turns: Turn[], patch: (prev: string) => string): Turn[] {
  for (let i = turns.length - 1; i >= 0; i--) {
    const t = turns[i]!;
    if (t.kind === 'assistant' && t.streaming) {
      const copy = turns.slice();
      copy[i] = { ...t, text: patch(t.text) };
      return copy;
    }
  }
  return turns;
}

function finalizeLastAssistant(turns: Turn[]): Turn[] {
  for (let i = turns.length - 1; i >= 0; i--) {
    const t = turns[i]!;
    if (t.kind === 'assistant' && t.streaming) {
      const copy = turns.slice();
      copy[i] = { ...t, streaming: false };
      return copy;
    }
  }
  return turns;
}

function toErrorText(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Unknown error';
}
